"use client";

import { useEffect, useState } from "react";

// Después de un deploy, el JS que ya está corriendo en las pestañas abiertas
// queda apuntando a chunks del build anterior. Si esa pestaña dispara un
// import dinámico más tarde (navegación, carga diferida), el chunk viejo ya
// no existe en el deployment actual -> 404 -> ChunkLoadError (ver
// app/global-error.tsx, que ya recupera automáticamente ese caso puntual).
// Este hook detecta el desfasaje ANTES de que eso pase, comparando el build
// con el que se armó esta pestaña (NEXT_PUBLIC_BUILD_ID, inline en el bundle
// al momento del build) contra el build que corre ahora en el servidor
// (/api/build-version, siempre sin cache). Así se puede avisar con tiempo en
// vez de esperar a que algo se rompa.
const CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutos

export function useBuildVersionCheck(): boolean {
  const [hasNewVersion, setHasNewVersion] = useState(false);

  useEffect(() => {
    const currentBuildId = process.env.NEXT_PUBLIC_BUILD_ID;
    // Sin build id no hay nada contra qué comparar (p.ej. en desarrollo local).
    if (!currentBuildId) return;

    let cancelled = false;

    const checkVersion = async () => {
      try {
        const res = await fetch("/api/build-version", { cache: "no-store" });
        if (!res.ok) return;
        const data: { buildId: string | null } = await res.json();
        if (!cancelled && data.buildId && data.buildId !== currentBuildId) {
          setHasNewVersion(true);
        }
      } catch {
        // Sin conexión o el endpoint falló -- no molestar al usuario por esto,
        // ya lo cubre el OfflineDialog si es un problema de red.
      }
    };

    // Chequear ahora, al volver a la pestaña, y cada tanto en segundo plano --
    // volver a la pestaña después de un rato es el momento más común en que
    // un usuario se encuentra con esto.
    checkVersion();
    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);
    const handleVisibility = () => {
      if (document.visibilityState === "visible") checkVersion();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  return hasNewVersion;
}
